import { useState, useEffect } from 'react';
import { getBranchenDefaults, generatePromptPreview } from '../../api/onboardingApi';
import { Bot, Shield, Zap, AlertTriangle, Eye, X, Plus, Loader2, FileText, ListChecks, Trash2, CheckCircle2 } from '../../icons/wp-icons';

const AUTONOMIE_LEVELS = [
  {
    key: 'BENACHRICHTIGUNG',
    label: 'Mit Rückfrage',
    icon: Shield,
    description: 'Ihr Assistent fragt Sie erst, bevor er z.B. Termine einträgt oder Angebote erstellt.',
    color: 'blue'
  },
  {
    key: 'AUTOMATISCH',
    label: 'Voll automatisch',
    icon: Zap,
    description: 'Ihr Assistent erledigt alles selbstständig und benachrichtigt Sie nur bei Wichtigem.',
    color: 'green'
  },
  {
    key: 'NUR_DATEN',
    label: 'Nur Daten sammeln',
    icon: Eye,
    description: 'Ihr Assistent sammelt nur Informationen und erstellt Anfragen. Keine automatischen Termine oder Angebote.',
    color: 'gray'
  }
];

function KiAgentStep({ data = {}, onDataChange, onboardingData }) {
  const branche = onboardingData?.branchenKonfiguration?.branche;

  const [autonomieLevel, setAutonomieLevel] = useState(data.autonomieLevel || 'BENACHRICHTIGUNG');
  const [nichtAngeboten, setNichtAngeboten] = useState(data.nichtAngeboten || []);
  const [angebotenLeistungen, setAngebotenLeistungen] = useState(data.angebotenLeistungen || []);
  const [pflichtfelder, setPflichtfelder] = useState(data.pflichtfelder || {});
  const [nichtAngebotenInput, setNichtAngebotenInput] = useState('');
  const [angebotenInput, setAngebotenInput] = useState('');
  const [vorschlaege, setVorschlaege] = useState([]);
  const [angebotenVorschlaege, setAngebotenVorschlaege] = useState([]);
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [promptPreview, setPromptPreview] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Pflichtfelder-Bearbeitung
  const [newFeldKey, setNewFeldKey] = useState('');
  const [newFeldLabel, setNewFeldLabel] = useState('');

  // KVA-Einstellungen
  const [kvaErstellungAktiv, setKvaErstellungAktiv] = useState(data.kvaErstellungAktiv || false);
  const [kvaBenachrichtigung, setKvaBenachrichtigung] = useState(data.kvaBenachrichtigung !== false);
  const [kvaAutoSenden, setKvaAutoSenden] = useState(data.kvaAutoSenden || false);

  // Branchen-Defaults laden
  useEffect(() => {
    const loadDefaults = async () => {
      if (branche && branche !== 'sonstiges') {
        try {
          const defaults = await getBranchenDefaults(branche);
          if (defaults.nichtAngebotenVorschlaege) {
            setVorschlaege(defaults.nichtAngebotenVorschlaege);
          }
          if (defaults.angebotenLeistungen) {
            setAngebotenVorschlaege(defaults.angebotenLeistungen);
            // Wenn noch keine Leistungen gesetzt, übernehme die Standard-Leistungen
            if (angebotenLeistungen.length === 0) {
              setAngebotenLeistungen(defaults.angebotenLeistungen);
            }
          }
          if (defaults.pflichtfelder && Object.keys(pflichtfelder).length === 0) {
            setPflichtfelder(defaults.pflichtfelder);
          }
        } catch (err) {
          console.error('Fehler beim Laden der Defaults:', err);
        }
      }
    };
    loadDefaults();
  }, [branche]);

  // Daten an Parent übergeben
  useEffect(() => {
    onDataChange({
      autonomieLevel,
      nichtAngeboten,
      angebotenLeistungen,
      pflichtfelder,
      kvaErstellungAktiv,
      kvaBenachrichtigung,
      kvaAutoSenden
    }, true);
  }, [autonomieLevel, nichtAngeboten, angebotenLeistungen, pflichtfelder, kvaErstellungAktiv, kvaBenachrichtigung, kvaAutoSenden]);

  const addNichtAngeboten = (text) => {
    const trimmed = text.trim();
    if (trimmed && !nichtAngeboten.includes(trimmed)) {
      setNichtAngeboten([...nichtAngeboten, trimmed]);
    }
    setNichtAngebotenInput('');
  };

  const removeNichtAngeboten = (index) => {
    setNichtAngeboten(nichtAngeboten.filter((_, i) => i !== index));
  };

  const addAngebotenLeistung = (text) => {
    const trimmed = text.trim();
    if (trimmed && !angebotenLeistungen.includes(trimmed)) {
      setAngebotenLeistungen([...angebotenLeistungen, trimmed]);
    }
    setAngebotenInput('');
  };

  const removeAngebotenLeistung = (index) => {
    setAngebotenLeistungen(angebotenLeistungen.filter((_, i) => i !== index));
  };

  // Pflichtfelder-Funktionen
  const handleTogglePflichtfeld = (key) => {
    setPflichtfelder(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        pflicht: !prev[key].pflicht
      }
    }));
  };

  const handleUpdateFeldLabel = (key, newLabel) => {
    setPflichtfelder(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        label: newLabel
      }
    }));
  };

  const handleRemovePflichtfeld = (key) => {
    setPflichtfelder(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const handleAddPflichtfeld = () => {
    if (!newFeldKey || !newFeldLabel) return;
    const cleanKey = newFeldKey.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    if (pflichtfelder[cleanKey]) {
      alert('Dieses Feld existiert bereits');
      return;
    }
    setPflichtfelder(prev => ({
      ...prev,
      [cleanKey]: {
        label: newFeldLabel,
        pflicht: true
      }
    }));
    setNewFeldKey('');
    setNewFeldLabel('');
  };

  const loadPromptPreview = async () => {
    setLoadingPreview(true);
    try {
      const result = await generatePromptPreview({
        branchenKonfiguration: {
          ...onboardingData?.branchenKonfiguration,
          nichtAngeboten,
          angebotenLeistungen,
          pflichtfelder
        },
        firmendaten: onboardingData?.firmendaten
      });
      setPromptPreview(result.systemPrompt);
      setShowPromptPreview(true);
    } catch (err) {
      console.error('Fehler beim Laden der Vorschau:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Ihr KI-Assistent
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Legen Sie fest, wie Ihr Assistent Kundenanfragen bearbeiten soll.
        </p>
      </div>

      {/* Autonomie-Level */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Wie selbstständig soll Ihr Assistent arbeiten?
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {AUTONOMIE_LEVELS.map((level) => {
            const Icon = level.icon;
            const isSelected = autonomieLevel === level.key;

            const colorMap = {
              blue: { border: '#378ab2', bg: '#378ab210', iconClass: 'bg-[#378ab2] text-white' },
              green: { border: '#22c55e', bg: '#22c55e10', iconClass: 'bg-green-500 text-white' },
              gray: { border: '#6b7280', bg: '#6b728010', iconClass: 'bg-gray-500 text-white' },
            };
            const colors = colorMap[level.color] || colorMap.blue;

            return (
              <button
                key={level.key}
                onClick={() => setAutonomieLevel(level.key)}
                className={`
                  p-4 rounded-xl border-2 text-left transition-all
                  ${isSelected
                    ? 'ring-2'
                    : 'border-gray-200 hover:border-gray-300'}
                `}
                style={isSelected ? {
                  borderColor: colors.border,
                  backgroundColor: colors.bg,
                  '--tw-ring-color': `${colors.border}33`
                } : {}}
              >
                <div className="flex items-start gap-3">
                  <div className={`
                    p-2 rounded-lg
                    ${isSelected ? colors.iconClass : 'bg-gray-100 text-gray-500'}
                  `}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{level.label}</div>
                    <div className="text-sm text-gray-600 mt-1">{level.description}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Angebotene Leistungen */}
      <div className="border-t pt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <CheckCircle2 className="w-4 h-4 inline mr-1 text-green-500" />
          Welche Leistungen bieten Sie an?
        </label>
        <p className="text-gray-500 text-sm mb-3">
          Ihr Assistent nennt diese Leistungen, wenn Kunden fragen was Sie anbieten.
        </p>

        {/* Eingabefeld */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={angebotenInput}
            onChange={(e) => setAngebotenInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addAngebotenLeistung(angebotenInput)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
            placeholder="z.B. Fliesenverlegung, Badrenovierung..."
          />
          <button
            onClick={() => addAngebotenLeistung(angebotenInput)}
            className="px-4 py-2 bg-[#378ab2] text-white rounded-lg hover:bg-[#2a6d8a] transition"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* Vorschläge */}
        {angebotenVorschlaege.length > 0 && (
          <div className="mb-3">
            <span className="text-xs text-gray-500">Vorschläge:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {angebotenVorschlaege.filter(v => !angebotenLeistungen.includes(v)).map((vorschlag, i) => (
                <button
                  key={i}
                  onClick={() => addAngebotenLeistung(vorschlag)}
                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition"
                >
                  + {vorschlag}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Ausgewählte */}
        {angebotenLeistungen.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {angebotenLeistungen.map((item, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
              >
                {item}
                <button
                  onClick={() => removeAngebotenLeistung(i)}
                  className="hover:text-green-900"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Nicht angebotene Leistungen */}
      <div className="border-t pt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <AlertTriangle className="w-4 h-4 inline mr-1 text-amber-500" />
          Was bieten Sie NICHT an?
        </label>
        <p className="text-gray-500 text-sm mb-3">
          Bei Anfragen zu diesen Themen lehnt Ihr Assistent freundlich ab.
        </p>

        {/* Eingabefeld */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={nichtAngebotenInput}
            onChange={(e) => setNichtAngebotenInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addNichtAngeboten(nichtAngebotenInput)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
            placeholder="z.B. Reparaturen, Notdienst..."
          />
          <button
            onClick={() => addNichtAngeboten(nichtAngebotenInput)}
            className="px-4 py-2 bg-[#378ab2] text-white rounded-lg hover:bg-[#2a6d8a] transition"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* Vorschläge */}
        {vorschlaege.length > 0 && (
          <div className="mb-3">
            <span className="text-xs text-gray-500">Vorschläge:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {vorschlaege.filter(v => !nichtAngeboten.includes(v)).map((vorschlag, i) => (
                <button
                  key={i}
                  onClick={() => addNichtAngeboten(vorschlag)}
                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition"
                >
                  + {vorschlag}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Ausgewählte */}
        {nichtAngeboten.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {nichtAngeboten.map((item, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm"
              >
                {item}
                <button
                  onClick={() => removeNichtAngeboten(i)}
                  className="hover:text-amber-900"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Pflichtfelder */}
      <div className="border-t pt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <ListChecks className="w-4 h-4 inline mr-1 text-[#378ab2]" />
          Welche Infos brauchen Sie vom Kunden?
        </label>
        <p className="text-gray-500 text-sm mb-4">
          Ihr Assistent fragt diese Informationen ab, bevor er dem Kunden einen Termin vorschlägt.
        </p>

        {/* Pflichtfelder Liste */}
        <div className="space-y-2 mb-4">
          {Object.entries(pflichtfelder).map(([key, config]) => (
            <div
              key={key}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={config.label}
                  onChange={(e) => handleUpdateFeldLabel(key, e.target.value)}
                  className="w-full px-2 py-1 border border-transparent rounded hover:border-gray-300 focus:border-[#378ab2] focus:outline-none text-sm font-medium bg-transparent"
                />
                <span className="text-xs text-gray-400">Feld: {key}</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer shrink-0">
                <span className="text-sm text-gray-500">Pflicht</span>
                <input
                  type="checkbox"
                  checked={config.pflicht}
                  onChange={() => handleTogglePflichtfeld(key)}
                  className="w-4 h-4 text-[#378ab2] rounded focus:ring-[#378ab2]"
                />
              </label>
              <button
                onClick={() => handleRemovePflichtfeld(key)}
                className="p-1.5 text-red-500 hover:bg-red-50 rounded transition shrink-0"
                title="Feld entfernen"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {Object.keys(pflichtfelder).length === 0 && (
            <p className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">
              Keine Pflichtfelder definiert
            </p>
          )}
        </div>

        {/* Neues Feld hinzufügen */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-sm font-medium text-gray-700 mb-2">Eigene Abfrage hinzufügen</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={newFeldKey}
              onChange={(e) => setNewFeldKey(e.target.value)}
              placeholder="Kurzname (z.B. farbe)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] text-sm"
            />
            <input
              type="text"
              value={newFeldLabel}
              onChange={(e) => setNewFeldLabel(e.target.value)}
              placeholder="Bezeichnung (z.B. Gewünschte Farbe)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] text-sm"
            />
            <button
              onClick={handleAddPflichtfeld}
              disabled={!newFeldKey || !newFeldLabel}
              className="px-4 py-2 bg-[#378ab2] text-white rounded-lg hover:bg-[#2a6d8a] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Hinzufügen</span>
            </button>
          </div>
        </div>
      </div>

      {/* KVA-Erstellung */}
      <div className="border-t pt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <FileText className="w-4 h-4 inline mr-1 text-[#378ab2]" />
          Automatische Angebote
        </label>
        <p className="text-gray-500 text-sm mb-4">
          Soll Ihr Assistent automatisch Kostenvoranschläge aus Ihrem Katalog erstellen können?
        </p>

        <div className="space-y-3">
          {/* KVA aktivieren */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">Angebote automatisch erstellen</div>
              <div className="text-sm text-gray-500">
                Ihr Assistent erstellt Kostenvoranschläge aus Ihrem Leistungskatalog
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={kvaErstellungAktiv}
                onChange={(e) => setKvaErstellungAktiv(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#378ab2]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#378ab2]"></div>
            </label>
          </div>

          {kvaErstellungAktiv && (
            <>
              {/* Benachrichtigung bei KVA */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg ml-4">
                <div>
                  <div className="font-medium text-gray-900">Vor dem Versand fragen</div>
                  <div className="text-sm text-gray-500">
                    Sie werden benachrichtigt und müssen jedes Angebot erst genehmigen
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={kvaBenachrichtigung}
                    onChange={(e) => setKvaBenachrichtigung(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#378ab2]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#378ab2]"></div>
                </label>
              </div>

              {/* KVA automatisch senden */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg ml-4">
                <div>
                  <div className="font-medium text-gray-900">Angebot automatisch per E-Mail senden</div>
                  <div className="text-sm text-gray-500">
                    Nach Ihrer Genehmigung wird das Angebot direkt an den Kunden geschickt
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={kvaAutoSenden}
                    onChange={(e) => setKvaAutoSenden(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#378ab2]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#378ab2]"></div>
                </label>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Prompt-Vorschau Button */}
      <div className="border-t pt-6">
        <button
          onClick={loadPromptPreview}
          disabled={loadingPreview}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
        >
          {loadingPreview ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Eye className="w-5 h-5" />
          )}
          Vorschau: So denkt Ihr Assistent
        </button>
      </div>

      {/* Prompt-Vorschau Modal */}
      {showPromptPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h4 className="font-medium text-gray-900">So arbeitet Ihr Assistent</h4>
              <button
                onClick={() => setShowPromptPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono bg-gray-50 p-4 rounded-lg">
                {promptPreview}
              </pre>
            </div>
            <div className="p-4 border-t bg-gray-50">
              <p className="text-sm text-gray-600">
                Diese Anweisungen bekommt Ihr Assistent, damit er Kundenanfragen richtig bearbeitet.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KiAgentStep;
